import type { CarouselEvent, SharedEventGroup } from '../types';

/**
 * Which shared events a "Shared with Me" slideshow plays, in what order.
 *
 * - One person's memories only: the group whose accountId is `ownerAccountId`
 *   (set by that person's "Play all" / card play button). Older links without
 *   it fall back to the group that contains `startAtId`. Mixing in anyone
 *   else's shared events is never right — each row on Shared with Me is a
 *   separate person's archive.
 * - The server's order, unchanged: oldest first, undated last — exactly the
 *   order of that person's row. (Re-sorting here used to put undated events
 *   at 1970, in the middle of the timeline.)
 */
export function selectSharedSlides(
  groups: SharedEventGroup[],
  ownerAccountId: string | null,
  startAtId: string | null,
): CarouselEvent[] {
  const byOwner = ownerAccountId ? groups.find(g => g.accountId === ownerAccountId) : undefined;
  const byStart = startAtId ? groups.find(g => g.events.some(e => e.id === startAtId)) : undefined;
  const group = byOwner ?? byStart ?? (groups.length === 1 ? groups[0] : undefined);
  if (group) return group.events;
  // No way to tell whose memories were meant: keep people together, in row order.
  return groups.flatMap(g => g.events);
}

/** A one-photo event holds for 15 s (as before); more photos share that time, but never under 6 s each. */
export const EVENT_MIN_MS = 15000;
export const PHOTO_MIN_MS = 6000;

export function photoDwellMs(photoCount: number): number {
  return Math.max(PHOTO_MIN_MS, Math.round(EVENT_MIN_MS / Math.max(1, photoCount)));
}
